import React, { useEffect, useState } from 'react';
import {
    BookOpenCheck, Plus, ListChecks, Pencil, FileText, Lock, ToggleLeft, ToggleRight,
    FilePlus,
    Trash2,
} from 'lucide-react';
import VectorChunkAuditModal from './VectorChunkAuditModal';
import UploadToKnowledgebaseModal from './UploadToKnowledgebaseModal';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import AddDocumentsToKbModal from './AddDocumentsModal';

const truncate = (text: string, max: number = 50) =>
    text.length > max ? text.substring(0, max) + '...' : text;

const KnowledgebaseManagementModal = ({ onClose,  workspaceid }: { onClose: () => void;  workspaceid: string }) => {
    const [kbs, setKbs] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [showChunks, setShowChunks] = useState(false);
    const [selectedChunks, setSelectedChunks] = useState<any[]>([]);
    const [selectedKb, setSelectedKb] = useState<any | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
    const [editKbId, setEditKbId] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);


    useEffect(() => {
        fetchKbs()
        if (workspaceid) fetchKbs();
    }, [   workspaceid]);
    const fetchKbs = async () => {
        try {
            const response = await knowledgebaseService.getKnowledgeBasesBySourceId(  workspaceid);
            setKbs(response);
        } catch (err) {
            console.error('Failed to load knowledge bases', err);
        }
    };
    const filteredKbs = kbs.filter(
        (kb) =>
            kb.title.toLowerCase().includes(search.toLowerCase()) ||
            kb.description.toLowerCase().includes(search.toLowerCase())
    );

    const handleViewChunks = async (kb: any) => {
        try {
            const docs = await knowledgebaseService.getDocuments(kb.id);
            setSelectedKb(kb);
            setSelectedChunks(docs);
            setShowChunks(true);
        } catch (err) {
            console.error('Failed to fetch documents:', err);
        }
    };

    const [kbToDelete, setKbToDelete] = useState<any | null>(null);
    const handleDeleteKnowledgeBase = async () => {
        if (!kbToDelete) return;

        try {
            await knowledgebaseService.deleteKnowledgeBase({
                knowledgeBaseId: kbToDelete.id,
                tenantId: "3fa85f64-5717-4562-b3fc-2c963f66afa6", // ideally from context
            });

            setKbs(prev => prev.filter(kb => kb.id !== kbToDelete.id));
            setKbToDelete(null);
        } catch (err) {
            console.error('Failed to delete KB:', err);
            alert('Failed to delete the knowledge base.');
            setKbToDelete(null);
        }
    };

    const handleViewDocuments = async (kb: any, dataSourceName: string) => {
        try {
            const responseFromApi = await knowledgebaseService.getUploadedDocuments(kb.id);
            setSelectedKb({ ...kb, sourceName: dataSourceName });
            setUploadedFiles(responseFromApi);
            setShowChunks(true);
        } catch (err) {
            console.error('Failed to fetch documents', err);
        }
    };
    const [showAddDocsModal, setShowAddDocsModal] = useState(false);
    const [targetKb, setTargetKb] = useState<any | null>(null); // for knowledge base metadata

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 text-[11px]">
            <div className="relative max-w-6xl mx-auto bg-neutral-900 text-white rounded-xl shadow-xl border border-neutral-800">
                <div className="px-6 pt-4 pb-3 border-b border-neutral-800">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold flex items-center gap-2 text-white">
                            <BookOpenCheck className="w-5 h-5 text-indigo-400" />
                            Knowledgebase Management
                        </h2>
                        <button
                            onClick={() => {
                                setIsEditMode(false);
                                setEditKbId(null);
                                setShowUploadModal(true);
                            }}
                            className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded text-[11px] font-medium shadow text-white"
                        >
                            <Plus className="w-4 h-4" /> Add New
                        </button>
                    </div>
                </div>

                <div className="px-6 pt-4 pb-2">
                    <input
                        type="text"
                        placeholder="Search by title or description..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-3 py-1.5 rounded bg-neutral-800 text-white outline-none border border-neutral-700 text-[11px] focus:ring-1 focus:ring-indigo-500"
                    />
                </div>

                <div className="overflow-x-auto px-4 pb-4">
                    <table className="w-full border-collapse min-w-[1200px] text-gray-300">
                        <thead className="bg-neutral-950 text-gray-400 sticky top-0 z-10">
                            <tr className="border-b border-neutral-800">
                                {['#', 'Title', 'Description', 'Entity','Roles', 'Users', 'Departments', 'Chunking', 'Denied Users', 'Audit Info'].map((h) => (
                                    <th key={h} className="px-3 py-2 font-medium whitespace-nowrap text-left">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-800">
                            {filteredKbs.map((kb, index) => (
                                <tr key={kb.id} className="hover:bg-neutral-800/40 transition">
                                    <td className="px-3 py-2 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => {
                                                setTargetKb(kb); // store selected knowledge base info
                                                setShowAddDocsModal(true);
                                            }} title="Add Data" className="text-blue-400 hover:text-blue-300 transition">
                                                <FilePlus className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleViewDocuments(kb, kb.title)}
                                                title="Logs"
                                                className="text-purple-400 hover:text-purple-300 transition"
                                            >
                                                <ListChecks className="w-4 h-4" />
                                            </button>
                                            <button title="Edit" onClick={() => {
                                                setIsEditMode(true);
                                                setEditKbId(kb.id);
                                                setShowUploadModal(true);
                                            }} className="text-yellow-400 hover:text-yellow-300 transition">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button title="Delete" onClick={() => setKbToDelete(kb)} className="text-red-400 hover:text-red-300 transition">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">{truncate(kb.title)}</td>
                                    <td className="px-3 py-2 max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">{truncate(kb.description)}</td>
                                    <td className="px-3 py-2 max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">{truncate(kb.entityName)}</td>
                                   <td className="px-3 py-2 max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">{truncate(kb.roles)}</td>
                                    <td className="px-3 py-2 max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">{truncate(kb.users)}</td>
                                    <td className="px-3 py-2 max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">{truncate(kb.departments)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{kb.chunking}</td>
                                    <td className="px-3 py-2 max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">{truncate(kb.deniedUsers || '-')}</td>
                                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{kb.auditInfo}</td>
                                    {/* <td className="px-3 py-2">
                                        <div className={`inline-flex items-center rounded-full px-2 py-0.5 cursor-pointer transition ${kb.status === 'Active' ? 'bg-green-600' : 'bg-gray-600'}`}>
                                            {kb.status === 'Active' ? (
                                                <><ToggleRight className="w-4 h-4 mr-1 text-white" /> Active</>
                                            ) : (
                                                <><ToggleLeft className="w-4 h-4 mr-1 text-white" /> Disabled</>
                                            )}
                                        </div>
                                    </td> */}
                                </tr>
                            ))}
                            {filteredKbs.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="text-center py-6 text-gray-500">No matching records.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="px-6 py-4 border-t border-neutral-800 bg-neutral-950 text-gray-500 flex items-center justify-between">
                    <span>Showing {filteredKbs.length} knowledge base(s)</span>
                    <button
                        onClick={onClose}
                        className="bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-1.5 rounded text-[11px] font-medium"
                    >
                        Close
                    </button>
                </div>
            </div>

            {showUploadModal &&  workspaceid && (
                <UploadToKnowledgebaseModal
                    tenantId="3fa85f64-5717-4562-b3fc-2c963f66afa6"
                    userId="f092eac4-3bf7-4133-8156-4f4d08436b77" 
                    isEditMode={isEditMode}
                    existingKbId={editKbId || undefined}
                    workspaceid={workspaceid}
                    onClose={() => {
                        setShowUploadModal(false);
                        setEditKbId(null);
                        setIsEditMode(false);
                    }}
                    onSubmit={() => {
                        setShowUploadModal(false);
                        setEditKbId(null);
                        setIsEditMode(false);
                        fetchKbs()
                        // optionally reload KBs
                    }}
                />
            )}


            {showChunks && selectedKb && (
                <VectorChunkAuditModal
                    kbTitle={selectedKb.title}
                    sourceName={selectedKb.sourceName || 'Unknown Source'} // replace with actual source name field
                    files={uploadedFiles} // this should be the array from /api/KnowledgeBase/documents
                    onClose={() => {
                        setShowChunks(false);
                        setSelectedChunks([]);
                        setSelectedKb(null);
                    }}
                />
            )}

            {kbToDelete && (
                <ConfirmDialog
                    message={`Are you sure you want to delete the knowledge base "${kbToDelete.title}" and all its documents?`}
                    onYes={handleDeleteKnowledgeBase}
                    onNo={() => setKbToDelete(null)}
                    onCancel={() => setKbToDelete(null)}
                />
            )}

            {showAddDocsModal && targetKb && (
                <AddDocumentsToKbModal
                    tenantId="3fa85f64-5717-4562-b3fc-2c963f66afa6" // Ideally from context
                    knowledgeBaseId={targetKb.id} 
                    kbTitle={targetKb.title}
                    onClose={() => {
                        setShowAddDocsModal(false);
                        setTargetKb(null);
                    }}
                />
            )}



        </div>
    );
};

export default KnowledgebaseManagementModal;
